import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, shareReplay, throwError } from "rxjs";
import { HotelToursCalendarParams, HotelToursCalendarResponse } from "../../../../types/interfaces/hotel-tour-search";
import { HttpHotelToursSearchService } from "../../../../services/search/hotel-tours-requests.service";

export interface ShortSearchAvailableDatesRequest {
  departCityId: number | string;
  countryId: number | string;
  nightsFrom: number;
  nightsTo: number;
  selectedHotelId?: number | string | null;
}

@Injectable({
  providedIn: "root",
})
export class ShortSearchCalendarDataService {
  private readonly availableDatesCache = new Map<string, Observable<string[]>>();

  constructor(
    private http: HttpClient,
    private httpSearchService: HttpHotelToursSearchService,
  ) {}

  getAvailableDateKeys(request: ShortSearchAvailableDatesRequest): Observable<string[]> {
    const cacheKey = this.getAvailableDateKeysCacheKey(request);
    const cached = this.availableDatesCache.get(cacheKey);

    if (cached) {
      return cached;
    }

    const params: Record<string, string> = {
      depart_city_id: String(request.departCityId),
      country_id: String(request.countryId),
      nightsFrom: String(request.nightsFrom),
      nightsTo: String(request.nightsTo),
    };

    if (request.selectedHotelId) {
      params.selected_hotel = String(request.selectedHotelId);
    }

    const request$ = this.http
      .get<{ dates?: string[] }>("/new/tours/findForm/getPriceCalendar", { params: new HttpParams({ fromObject: params }) })
      .pipe(
        map((response) => Array.from(new Set(
          (response?.dates || [])
            .map((value) => this.parseCalendarResponseDate(value))
            .filter((value): value is string => !!value)
        )).sort()),
        shareReplay(1),
        catchError((error) => {
          this.availableDatesCache.delete(cacheKey);
          return throwError(() => error);
        })
      );

    this.availableDatesCache.set(cacheKey, request$);

    return request$;
  }

  getHotelPriceCalendar(bodyParams: { params: HotelToursCalendarParams }): Observable<HotelToursCalendarResponse> {
    return this.httpSearchService.getHotelPriceCalendar(bodyParams);
  }

  getHotelPriceCalendarCacheKey(bodyParams: { params: HotelToursCalendarParams }) {
    return this.httpSearchService.getHotelPriceCalendarCacheKey(bodyParams);
  }

  private getAvailableDateKeysCacheKey(request: ShortSearchAvailableDatesRequest) {
    return JSON.stringify({
      departCityId: String(request.departCityId),
      countryId: String(request.countryId),
      nightsFrom: Number(request.nightsFrom),
      nightsTo: Number(request.nightsTo),
      selectedHotelId: request.selectedHotelId ? String(request.selectedHotelId) : null,
    });
  }

  private parseCalendarResponseDate(value: string) {
    if (!value) {
      return null;
    }

    if (value.includes(".")) {
      const [day, month, year] = value.split(".");
      if (!day || !month || !year) {
        return null;
      }

      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }

    if (value.includes("-")) {
      return value.slice(0, 10);
    }

    return null;
  }
}
